/**
 * ProfitSpot Main App — Responsive Layout
 * Desktop: fixed sidebar | Mobile: top header + drawer
 * Landing page at /, App at /explore and other routes
 */
import { useState } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from 'sonner';
import { Menu, Zap } from 'lucide-react';

// Layout
import Sidebar from './components/layout/Sidebar';
import TrialBanner from './components/TrialBanner';

// Protected Routes
import { AdminRoute, PremiumRoute } from './components/ProtectedRoute';

// Pages
import LandingPage from './pages/LandingPage';
import YieldExplorer from './pages/YieldExplorer';
import SimulatorLabs from './pages/SimulatorLabs';
import WhaleTracker from './pages/WhaleTracker';
import NodeHealth from './pages/NodeHealth';
import AdminDashboard from './pages/AdminDashboard';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Unsubscribe from './pages/Unsubscribe';
import Disclaimer from './pages/Disclaimer';
import AlertPreferences from './pages/AlertPreferences';
import ForecastDialPage from './pages/ForecastDialPage';
import PortfolioTracker from './pages/PortfolioTracker';
import PaymentSuccess from './pages/PaymentSuccess';
import AdvisorsPage from './pages/AdvisorsPage';
import OnboardingTour from './components/OnboardingTour';

function AppContent() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('profitspot_sidebar_collapsed') === 'true'
  );

  return (
    <div className="flex min-h-screen bg-[#0a0a12]">
      <Sidebar
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        onCollapsedChange={setSidebarCollapsed}
      />

      {/* Main content — full width on mobile, offset matches sidebar width on desktop */}
      <main className={`flex-1 w-full transition-all duration-300 flex flex-col min-w-0 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-56'
      }`}>

        {/* ── Mobile Top Header ── */}
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-[#0a0a12]/95 backdrop-blur-sm border-b border-gray-800">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-[#00ff88]" />
            <span className="font-bold text-sm tracking-tight text-white">
              PROFIT<span className="text-[#00ff88]">SPOT</span>
            </span>
          </div>
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            data-testid="mobile-menu-btn"
            aria-label="Open navigation menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </header>

        {/* Trial Banner */}
        <TrialBanner />

        {/* Page Content */}
        <div className="flex-1 min-w-0">
          <Routes>
            <Route path="/explore" element={<YieldExplorer />} />
            <Route path="/simulator" element={<SimulatorLabs />} />
            <Route path="/whales" element={<WhaleTracker />} />
            <Route
              path="/node-health"
              element={
                <AdminRoute>
                  <NodeHealth />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-dashboard"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/alert-preferences" element={<AlertPreferences />} />
            <Route path="/forecast" element={<ForecastDialPage />} />
            <Route path="/portfolio" element={<PortfolioTracker />} />
          </Routes>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-800/50 px-4 md:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-gray-600 text-center sm:text-left">
            © 2026 ProfitSpot. Non-custodial DeFi intelligence.
          </p>
          <div className="flex items-center gap-3 text-xs">
            <Link to="/terms" className="text-gray-600 hover:text-gray-400 transition-colors">Terms</Link>
            <span className="text-gray-800">·</span>
            <Link to="/privacy" className="text-gray-600 hover:text-gray-400 transition-colors">Privacy</Link>
            <span className="text-gray-800">·</span>
            <Link to="/disclaimer" className="text-gray-600 hover:text-gray-400 transition-colors">Disclaimer</Link>
          </div>
        </footer>
      </main>

      <Toaster
        position="bottom-right"
        toastOptions={{
          style: { background: '#1a1a2e', border: '1px solid #333', color: '#fff' },
        }}
      />
      <OnboardingTour />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Landing page — no sidebar, marketing page */}
          <Route path="/" element={<LandingPage />} />
          {/* Standalone route — no sidebar, works from email links without login */}
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          {/* Payment success page — standalone, no sidebar */}
          <Route path="/payment-success" element={<PaymentSuccess />} />
          {/* Advisors landing page — standalone, no sidebar */}
          <Route path="/advisors" element={<AdvisorsPage />} />
          {/* All other routes get the full app shell with sidebar */}
          <Route path="*" element={<AppContent />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
